'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Radio, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';

interface LoginFormValues {
  email: string;
  password: string;
  remember: boolean;
}

const DEMO_EMAIL = 'admin@rumbaexplosions.com';
const DEMO_PASSWORD = 'broadcast2026!';

export default function LoginContent() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFormValues>({ defaultValues: { remember: false } });

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofill = () => {
    setValue('email', DEMO_EMAIL);
    setValue('password', DEMO_PASSWORD);
  };

  const onSubmit = async (data: LoginFormValues) => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1400));
    setLoading(false);

    // Backend integration point — replace with real auth call
    if (data.email === DEMO_EMAIL && data.password === DEMO_PASSWORD) {
      toast.success('Welcome back — redirecting to Station HQ');
      setTimeout(() => { window.location.href = '/admin-dashboard'; }, 800);
    } else {
      setError('root', {
        message: 'Invalid credentials — use the demo account below to sign in',
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 flex-col public-hero-bg relative overflow-hidden p-12">
        <div className="flex items-center gap-3 mb-auto">
          <AppLogo size={40} />
          <span className="font-bold text-xl text-foreground">Rumbaexplosions</span>
        </div>

        <div className="mb-auto">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-2.5 h-2.5 rounded-full bg-success live-pulse" />
            <span className="text-sm font-semibold text-success">Stream Currently Live</span>
          </div>
          <h1 className="text-4xl xl:text-5xl font-bold text-foreground leading-tight mb-4">
            Your automated<br />
            <span className="text-gradient-primary">internet radio</span><br />
            station
          </h1>
          <p className="text-muted-foreground text-lg max-w-md leading-relaxed">
            Upload music, schedule show blocks, and broadcast to the world — automatically. No manual mixing required.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {[
            { id: 'stat-listeners', value: '247', label: 'Live Listeners' },
            { id: 'stat-tracks', value: '1,284', label: 'Tracks in Library' },
            { id: 'stat-shows', value: '6', label: 'Shows Today' },
          ].map((s) => (
            <div key={s.id} className="glass-card rounded-xl p-4">
              <p className="text-2xl font-bold text-foreground tabular-nums">{s.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Decorative radio waves */}
        <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 pointer-events-none">
          {[120, 180, 240, 300].map((size, i) => (
            <div
              key={`wave-ring-${i + 1}`}
              className="absolute rounded-full border border-primary/10"
              style={{ width: size, height: size, top: -size / 2, left: -size / 2 }}
            />
          ))}
          <div className="w-8 h-8 rounded-full bg-primary/20 border border-primary/40" />
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-8">
            <AppLogo size={32} />
            <span className="font-bold text-lg text-foreground">Rumbaexplosions</span>
          </div>

          <div className="mb-8">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 border border-primary/20 rounded-full mb-4">
              <Radio size={12} className="text-primary" />
              <span className="text-xs font-semibold text-primary">Admin Portal</span>
            </div>
            <h2 className="text-2xl font-bold text-foreground">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">Access your station control panel</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Root Error */}
            {errors.root && (
              <div className="flex items-start gap-2.5 px-3 py-2.5 bg-danger/10 border border-danger/20 rounded-lg">
                <AlertCircle size={15} className="text-danger flex-shrink-0 mt-0.5" />
                <p className="text-xs text-danger leading-snug">{errors.root.message}</p>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Email address</label>
              <input
                type="email"
                placeholder="admin@rumbaexplosions.com"
                className={`input-field ${errors.email ? 'border-danger focus:border-danger focus:ring-danger/30' : ''}`}
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                })}
              />
              {errors.email && <p className="text-xs text-danger mt-1">{errors.email.message}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  className={`input-field pr-10 ${errors.password ? 'border-danger focus:border-danger focus:ring-danger/30' : ''}`}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-danger mt-1">{errors.password.message}</p>}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remember"
                className="w-4 h-4 rounded border-border bg-input accent-primary"
                {...register('remember')}
              />
              <label htmlFor="remember" className="text-xs text-muted-foreground">Keep me signed in</label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center py-2.5 disabled:opacity-70"
            >
              {loading ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Signing in...
                </>
              ) : 'Sign in to Station HQ'}
            </button>
          </form>

          {/* Demo Credentials - hidden from public */}

          <p className="text-center text-xs text-muted-foreground mt-6">
            <Link href="/public-listener-page" className="text-accent hover:text-accent/80 transition-colors">
              Listen to the public stream →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}